import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function AreasPage() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Areas We Serve | Painters & Decorators Hertfordshire | Brushour',
    description: 'Brushour provides professional painting and decorating services across Hertfordshire, Buckinghamshire, and Greater London – Watford, Rickmansworth, Chorleywood, Northwood, Pinner, Bushey, Radlett, and Gerrards Cross.',
    keywords: 'painters Hertfordshire, decorators Buckinghamshire, painting services Greater London, local painters Watford, areas served Hertfordshire',
    canonical: `${SITE_URL}/areas`,
    schema: [
      generateWebPageSchema('Areas We Serve – Brushour Painters & Decorators', 'Professional painting and decorating services across Hertfordshire, Buckinghamshire, and Greater London', `${SITE_URL}/areas`),
      generateBreadcrumbSchema([
        { name: 'Home', url: SITE_URL },
        { name: 'Areas', url: `${SITE_URL}/areas` },
      ]),
    ],
  });

  const areas = [
    {
      name: 'Chorleywood',
      region: 'Hertfordshire',
      slug: 'chorleywood',
      image: 'https://public.readdy.ai/ai/img_res/09ffac11893c0f660e0faf858964e35e.jpg',
      description: 'Professional painting and decorating services throughout Chorleywood and surrounding villages.',
    },
    {
      name: 'Rickmansworth',
      region: 'Hertfordshire',
      slug: 'rickmansworth',
      image: 'https://readdy.ai/api/search-image?query=picturesque%20town%20center%20of%20Rickmansworth%20Hertfordshire%20featuring%20traditional%20architecture%20historic%20buildings%20canal%20views%20and%20vibrant%20community%20setting%20ideal%20for%20painting%20and%20decorating%20services&width=600&height=400&seq=area002&orientation=landscape',
      description: 'Expert painters and decorators serving Rickmansworth, Mill End, and West Hyde areas.',
    },
    {
      name: 'Northwood',
      region: 'Greater London',
      slug: 'northwood',
      image: 'https://readdy.ai/api/search-image?query=elegant%20residential%20area%20in%20Northwood%20London%20showing%20upscale%20properties%20tree%20lined%20streets%20well%20kept%20gardens%20and%20prestigious%20neighborhood%20perfect%20for%20high%20quality%20painting%20and%20decorating%20services&width=600&height=400&seq=area003&orientation=landscape',
      description: 'Premium painting services for homes and businesses across Northwood and Northwood Hills.',
    },
    {
      name: 'Pinner',
      region: 'Greater London',
      slug: 'pinner',
      image: 'https://readdy.ai/api/search-image?query=charming%20historic%20village%20of%20Pinner%20London%20featuring%20traditional%20Tudor%20style%20buildings%20quaint%20high%20street%20vibrant%20local%20community%20and%20beautiful%20residential%20properties%20ideal%20for%20painting%20services&width=600&height=400&seq=area004&orientation=landscape',
      description: 'Trusted painting and decorating solutions for Pinner Village, Pinner Green, and Hatch End.',
    },
    {
      name: 'Bushey',
      region: 'Hertfordshire',
      slug: 'bushey',
      image: 'https://readdy.ai/api/search-image?query=attractive%20suburban%20area%20of%20Bushey%20Hertfordshire%20showing%20family%20homes%20green%20spaces%20local%20amenities%20and%20welcoming%20community%20environment%20perfect%20for%20professional%20painting%20and%20decorating%20services&width=600&height=400&seq=area005&orientation=landscape',
      description: 'Quality painting services covering Bushey Village, Bushey Heath, and Oxhey.',
    },
    {
      name: 'Radlett',
      region: 'Hertfordshire',
      slug: 'radlett',
      image: 'https://readdy.ai/api/search-image?query=prosperous%20village%20of%20Radlett%20Hertfordshire%20displaying%20quality%20residential%20properties%20leafy%20streets%20excellent%20local%20facilities%20and%20affluent%20neighborhood%20atmosphere%20ideal%20for%20premium%20painting%20services&width=600&height=400&seq=area006&orientation=landscape',
      description: 'Experienced painters serving Radlett, Aldenham, and surrounding communities.',
    },
    {
      name: 'Gerrards Cross',
      region: 'Buckinghamshire',
      slug: 'gerrards-cross',
      image: 'https://readdy.ai/api/search-image?query=prestigious%20town%20of%20Gerrards%20Cross%20Buckinghamshire%20featuring%20luxury%20properties%20elegant%20architecture%20beautiful%20countryside%20setting%20and%20exclusive%20residential%20areas%20perfect%20for%20high%20end%20painting%20services&width=600&height=400&seq=area007&orientation=landscape',
      description: 'Premium painting and decorating for Gerrards Cross, Fulmer, and Chalfont St Peter.',
    },
    {
      name: 'Watford',
      region: 'Hertfordshire',
      slug: 'watford',
      image: 'https://readdy.ai/api/search-image?query=vibrant%20town%20centre%20of%20Watford%20Hertfordshire%20featuring%20modern%20and%20traditional%20architecture%20busy%20high%20street%20well%20maintained%20buildings%20and%20lively%20community%20atmosphere%20perfect%20for%20professional%20painting%20and%20decorating%20services&width=600&height=400&seq=area008&orientation=landscape',
      description: 'Professional painting and decorating services across Watford, Oxhey, Carpenders Park, and Garston.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20beautiful%20Hertfordshire%20and%20Buckinghamshire%20countryside%20showing%20multiple%20towns%20and%20villages%20with%20residential%20areas%20green%20spaces%20and%20community%20settings%20perfect%20for%20local%20painting%20services%20coverage&width=1920&height=800&seq=areahero001&orientation=landscape"
            alt="Areas we serve"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Areas We Serve
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed">
            Professional painting and decorating services across Hertfordshire, 
            Buckinghamshire, and Greater London
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Local Painting Services
            </h2>
            <p className="text-lg text-sky-600 font-medium">
              Bringing quality painting and decorating to your community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {areas.map((area, index) => (
              <Link
                key={index}
                to={`/areas/${area.slug}`}
                className="group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="w-full h-64 overflow-hidden">
                  <img
                    src={area.image}
                    alt={`Painting services in ${area.name}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors">
                    {area.name}
                  </h3>
                  <p className="text-sm text-sky-600 font-medium mb-3">
                    {area.region}
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed mb-4">
                    {area.description}
                  </p>
                  <div className="flex items-center text-sky-600 font-medium">
                    <span className="text-sm">Learn more</span>
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Don't See Your Area?
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mb-8">
            We also serve many other locations across Hertfordshire, Buckinghamshire, 
            and Greater London. Contact us to discuss your painting and decorating needs, 
            and we'll be happy to help.
          </p>
          <a
            href="/booking"
            className="inline-block px-8 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-lg transition-colors duration-300 whitespace-nowrap"
          >
            Get a Free Quote
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}